import { Route } from '@angular/router';
import { UserRole } from '@models';
import { authGuard } from './guards/auth.guard';
import { loginGuard } from './guards/login.guard';
import { superAdminGuard } from './guards/super-admin.guard';
import { planActiveGuard } from './guards/plan-active.guard';
import { moduleGuard } from './guards/module.guard';
import { federationOnlyGuard } from './guards/federation-only.guard';
import { managerOnlyGuard } from './guards/manager-only.guard';

export const appRoutes: Route[] = [
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/onboarding/login/login').then((m) => m.LoginComponent),
    canActivate: [loginGuard()],
  },
  {
    path: 'recovery',
    loadComponent: () =>
      import('./pages/onboarding/recovery/recovery').then(
        (m) => m.RecoveryComponent,
      ),
  },
  {
    path: 'new-password',
    loadComponent: () =>
      import('./pages/onboarding/new-password/new-password').then(
        (m) => m.NewPasswordComponent,
      ),
  },
  {
    path: 'platform',
    loadComponent: () =>
      import('./components/platform-layout/platform-layout.component').then(
        (m) => m.PlatformLayoutComponent,
      ),
    canActivate: [superAdminGuard()],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('./pages/platform/platform.routes').then(
            (r) => r.PLATFORM_ROUTES,
          ),
      },
    ],
  },
  {
    path: 'plan-expired',
    loadComponent: () =>
      import('./pages/onboarding/plan-expired/plan-expired.component').then(
        (m) => m.PlanExpiredComponent,
      ),
  },
  {
    // Public — no auth guard. Token validation happens inside the CF.
    path: 'tenant-onboarding',
    loadChildren: () =>
      import('./pages/tenant-onboarding/tenant-onboarding.routes').then(
        (r) => r.TENANT_ONBOARDING_ROUTES,
      ),
  },
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./components/layout/layout.component').then(
        (m) => m.AppLayoutComponent,
      ),
    canActivate: [
      authGuard(
        [UserRole.ADMIN, UserRole.REFEREE_COORDINATOR, UserRole.MANAGER],
        true,
      ),
      planActiveGuard(),
    ],
    children: [
      // ── Default redirect ────────────────────────────────────────────────
      { path: '', pathMatch: 'full', redirectTo: 'home' },

      // ── Club manager portal — MANAGER role only ─────────────────────────
      // A club delegate (MANAGER) within a federation lands here exclusively.
      // ADMIN / REFEREE_COORDINATOR attempting /dashboard/club are redirected
      // to /dashboard/home.
      {
        path: 'club',
        canActivate: [managerOnlyGuard()],
        loadChildren: () =>
          import('./pages/club-manager/club-manager.routes').then(
            (r) => r.CLUB_MANAGER_ROUTES,
          ),
      },

      // ── Federation-admin routes — ADMIN & REFEREE_COORDINATOR only ───────
      // MANAGER role navigating to any of these routes is redirected to
      // /dashboard/club. All existing module guards are preserved inside.
      {
        path: '',
        canActivateChild: [federationOnlyGuard()],
        children: [
          {
            path: 'home',
            loadComponent: () =>
              import('./pages/home/home.component').then(
                (m) => m.HomeComponent,
              ),
          },
          {
            path: 'users',
            loadChildren: () =>
              import('./pages/users/users.routes').then((r) => r.USERS_ROUTES),
          },
          {
            path: 'clubs',
            loadChildren: () =>
              import('./pages/clubs/clubs.routes').then((r) => r.CLUBS_ROUTES),
          },
          {
            path: 'teams',
            loadChildren: () =>
              import('./pages/teams/teams.routes').then((r) => r.TEAMS_ROUTES),
          },
          {
            path: 'competitions',
            loadChildren: () =>
              import('./pages/competitions/competitions.routes').then(
                (r) => r.COMPETITIONS_ROUTES,
              ),
          },
          {
            // Widgets web (#513). Sin rutas hijas: todo vive en esta pantalla.
            // Como 'competitions', sin moduleGuard: 'competitions' solo existe
            // como módulo de club y el menú ya filtra con has('competitions').
            path: 'embed',
            loadComponent: () =>
              import('./pages/embed/embed-widget.page').then(
                (m) => m.EmbedWidgetPage,
              ),
          },
          {
            path: 'seasons',
            loadChildren: () =>
              import('./pages/seasons/seasons.routes').then(
                (r) => r.SEASONS_ROUTES,
              ),
          },
          {
            path: 'reports',
            // federation: 'advanced_reports' module | club: 'reports' module
            canActivate: [moduleGuard(['advanced_reports', 'reports'])],
            loadChildren: () =>
              import('./pages/reports/reports.routes').then(
                (r) => r.REPORTS_ROUTES,
              ),
          },
          {
            path: 'license-rates',
            loadChildren: () =>
              import('./pages/license-rates/license-rates.routes').then(
                (r) => r.LICENSE_RATES_ROUTES,
              ),
          },
          {
            path: 'receipts',
            canActivate: [moduleGuard('receipts')],
            loadChildren: () =>
              import('./pages/receipts/receipts.routes').then(
                (r) => r.RECEIPTS_ROUTES,
              ),
          },
          {
            path: 'invoices',
            canActivate: [moduleGuard('invoices')],
            loadChildren: () =>
              import('./pages/invoices/invoices.routes').then(
                (r) => r.INVOICES_ROUTES,
              ),
          },
          {
            path: 'emails',
            canActivate: [moduleGuard('communications')],
            loadChildren: () =>
              import('./pages/emails/emails.routes').then(
                (r) => r.EMAILS_ROUTES,
              ),
          },
          {
            path: 'notifications',
            canActivate: [moduleGuard('communications')],
            loadChildren: () =>
              import('./pages/notifications/notifications.routes').then(
                (r) => r.NOTIFICATIONS_ROUTES,
              ),
          },
          {
            path: 'calendar',
            loadChildren: () =>
              import('./pages/calendar/calendar.routes').then(
                (r) => r.CALENDAR_ROUTES,
              ),
          },
          {
            path: 'acta/:matchId',
            loadComponent: () =>
              import('./pages/acta/acta.component').then(
                (m) => m.ActaComponent,
              ),
          },
          {
            path: 'change-dates',
            canActivate: [moduleGuard('change_dates')],
            loadChildren: () =>
              import('./pages/change-dates/change-dates.routes').then(
                (r) => r.CHANGE_DATES_ROUTES,
              ),
          },
          {
            path: 'incidents',
            canActivate: [moduleGuard('sanctions')],
            loadChildren: () =>
              import('./pages/incidents/incidents.routes').then(
                (r) => r.INCIDENTS_ROUTES,
              ),
          },
          {
            path: 'transfers',
            canActivate: [moduleGuard('transfers')],
            loadChildren: () =>
              import('./pages/transfers/transfers.routes').then(
                (r) => r.TRANSFERS_ROUTES,
              ),
          },
          {
            path: 'sponsors',
            canActivate: [moduleGuard('sponsors')],
            loadChildren: () =>
              import('./pages/sponsors/sponsors.routes').then(
                (r) => r.SPONSORS_ROUTES,
              ),
          },
          {
            path: 'accounting',
            loadChildren: () =>
              import('./pages/accounting/accounting.routes').then(
                (r) => r.ACCOUNTING_ROUTES,
              ),
          },
          {
            path: 'referees',
            loadChildren: () =>
              import('./pages/referees/referees.routes').then(
                (r) => r.REFEREES_ROUTES,
              ),
          },
          {
            path: 'memberships',
            canActivate: [moduleGuard('memberships')],
            loadChildren: () =>
              import('./pages/memberships/memberships.routes').then(
                (r) => r.MEMBERSHIPS_ROUTES,
              ),
          },
          {
            path: 'availability',
            canActivate: [moduleGuard('squad_availability')],
            loadComponent: () =>
              import('./pages/availability/availability.component').then(
                (m) => m.AvailabilityComponent,
              ),
          },
          {
            path: 'call-ups',
            canActivate: [moduleGuard('call_ups')],
            loadComponent: () =>
              import('./pages/call-ups/call-ups.component').then(
                (m) => m.CallUpsComponent,
              ),
          },
          {
            // Venue catalog (#722). Deliberately WITHOUT
            // moduleGuard('reservations'): a federation without the
            // reservations module still needs to manage its venues (R7).
            path: 'facilities',
            loadChildren: () =>
              import('./pages/facilities/facilities.routes').then(
                (r) => r.FACILITIES_ROUTES,
              ),
          },
          {
            path: 'reservations',
            canActivate: [moduleGuard('reservations')],
            loadChildren: () =>
              import('./pages/reservations/reservations.routes').then(
                (r) => r.RESERVATIONS_ROUTES,
              ),
          },
          {
            path: 'technification',
            canActivate: [federationOnlyGuard(), moduleGuard('technification')],
            loadChildren: () =>
              import('./pages/technification/technification.routes').then(
                (r) => r.TECHNIFICATION_ROUTES,
              ),
          },
          {
            path: 'news',
            canActivate: [moduleGuard('communications')],
            loadChildren: () =>
              import('./pages/news/news.routes').then((r) => r.NEWS_ROUTES),
          },
          {
            path: 'support',
            loadChildren: () =>
              import('./pages/support/support.routes').then(
                (r) => r.SUPPORT_ROUTES,
              ),
          },
          {
            path: 'subscription',
            loadComponent: () =>
              import('./pages/subscription/subscription.page').then(
                (m) => m.SubscriptionPage,
              ),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./pages/settings/settings.page').then(
                (m) => m.SettingsPage,
              ),
          },
        ],
      },
    ],
  },
  { path: '', pathMatch: 'full', redirectTo: '/dashboard' },
  { path: '**', redirectTo: '/dashboard' },
];
